import { useCallback, useEffect, useState } from 'react'
import {
  deleteDefaultFile,
  deleteLibertyDocument,
  fetchLibertyDocuments,
  fetchPolicyDeclarationFiles,
  uploadLibertyDocument,
} from '../services/residentApi'
import type { LibertyDocumentRow } from '../types'

export function useResidentDocuments(
  onRefresh?: () => Promise<void> | void,
  defaultId?: string | number,
  /** Angular gates `loadDocuments()` on `liberty_roleslist`. */
  canLoadLibertyDocuments = false,
  /** Page-scoped default-document deletes protect against table-specific id collisions. */
  apiPage: 'resident' | 'default' = 'default',
) {
  const [processingKey, setProcessingKey] = useState<string | null>(null)
  const [libertyDocuments, setLibertyDocuments] = useState<LibertyDocumentRow[]>([])

  const loadLibertyDocuments = useCallback(async () => {
    if (!defaultId || !canLoadLibertyDocuments) {
      setLibertyDocuments([])
      return
    }
    try {
      const response = await fetchLibertyDocuments(defaultId)
      setLibertyDocuments(Array.isArray(response) ? (response as LibertyDocumentRow[]) : [])
    } catch {
      setLibertyDocuments([])
    }
  }, [canLoadLibertyDocuments, defaultId])

  useEffect(() => {
    void loadLibertyDocuments()
  }, [loadLibertyDocuments])

  const removeDefaultFile = useCallback(
    async (fileId: string | number) => {
      setProcessingKey(`default-${fileId}`)
      try {
        await deleteDefaultFile(fileId, { page: apiPage })
        await onRefresh?.()
      } finally {
        setProcessingKey(null)
      }
    },
    [apiPage, onRefresh],
  )

  /** Single or batch — mirrors Angular `uploadDocuments()` (`attachment[0]`, `attachment[1]`, …). */
  const addLibertyDocuments = useCallback(
    async (files: File | File[]) => {
      const list = Array.isArray(files) ? files : [files]
      if (!defaultId || !list.length) return
      setProcessingKey('liberty-upload')
      try {
        const formData = new FormData()
        list.forEach((file, index) => {
          formData.append(`attachment[${index}]`, file)
        })
        await uploadLibertyDocument(defaultId, formData)
        await loadLibertyDocuments()
        await onRefresh?.()
      } finally {
        setProcessingKey(null)
      }
    },
    [defaultId, loadLibertyDocuments, onRefresh],
  )

  const removeLibertyDocument = useCallback(
    async (noteId: string | number) => {
      setProcessingKey(`liberty-${noteId}`)
      try {
        await deleteLibertyDocument(noteId)
        await loadLibertyDocuments()
      } finally {
        setProcessingKey(null)
      }
    },
    [loadLibertyDocuments],
  )

  const loadPolicyDeclarationFiles = useCallback(async (applicationId: string | number) => {
    setProcessingKey(`policyDecl-${applicationId}`)
    try {
      return await fetchPolicyDeclarationFiles(applicationId)
    } finally {
      setProcessingKey(null)
    }
  }, [])

  return {
    processingKey,
    libertyDocuments,
    loadLibertyDocuments,
    removeDefaultFile,
    addLibertyDocuments,
    removeLibertyDocument,
    loadPolicyDeclarationFiles,
  }
}
